<script setup lang="ts">
import { h, ref } from 'vue';
import { toRefs, watchEffect } from 'vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import icons from './icons';
import { getContent } from './templates';
import { AksInsertProductCardButton, PLUGIN_KEY } from './config';
import { EditorInteractionManager, ImageCdnService, UseDrawerComposableService } from '../types';
import ProductCardDrawer from './product-card-drawer.vue';
import { Amount } from '@client/monolith/core/types/amount';
import usePrice from '@client/monolith/core/composables/use-price';
import { CaptureErrorFunc } from '../../use-error-logger';

const isContainerNode = (node: Element) => node.hasAttribute(`data-${PLUGIN_KEY}`);
const isChildNode = (node: Element) => node.hasAttribute(`data-${PLUGIN_KEY}-child`);

enum ModalMode {
  Insert = 'insert',
  Edit = 'edit',
  Closed = 'closed',
}

defineOptions({ name: 'AksInsertProductCardPlugin' });

const emit = defineEmits(['toolbarButtonClicked', 'contentInserted'])

const props = defineProps<{
  id: string;
  editorInteractionManager: EditorInteractionManager;
  useDrawerService: UseDrawerComposableService;
  imageCdnService: ImageCdnService;
  captureError: CaptureErrorFunc;
}>();

const { id, editorInteractionManager: im, imageCdnService } = toRefs(props);
const { t } = useI18n();

const selectedNode = ref<Element | null>(null);
const modalMode = ref<ModalMode>(ModalMode.Closed);

const productCardDrawer = props.useDrawerService();

const pluginId = computed(() => id.value + '_' + PLUGIN_KEY);

const register = () => {
  im.value.registerPlugin(pluginId.value, ({ addButton, addContextToolbar, addIcons, on }) => {
      addIcons(icons);

      addButton(AksInsertProductCardButton.InsertProductCard, {
        text: t('account.brands.createCampaigns.editor.toolbar.insertProductCard.label'),
        icon: 'tag',
        onAction: () => {
          modalMode.value = ModalMode.Insert;
          openProductCardDrawer();
          emit('toolbarButtonClicked', PLUGIN_KEY);
        }
      });

      addButton(AksInsertProductCardButton.EditProductCard, {
        icon: 'edit-block',
        onAction: () => {
          if (selectedNode.value) {
            modalMode.value = ModalMode.Edit;
            openProductCardDrawer();
          }
        }
      });

      addButton(AksInsertProductCardButton.RemoveProductCard, {
        icon: 'trash',
        onAction: async () => {
          if (selectedNode.value) {
            removeSelectedProductCards();
          }
        }
      });

      addContextToolbar('selection', {
        predicate: (node) => {
          if (node?.hasAttribute(`data-${PLUGIN_KEY}`)) {
            im.value.selectNode(node);
            selectedNode.value = node;
            return true;
          }

          return false;
        },
        items: [AksInsertProductCardButton.EditProductCard, AksInsertProductCardButton.RemoveProductCard].join(' '),
        position: 'selection',
        scope: 'node'
      });

      const selectContainer = (e: KeyboardEvent) => {
        const currentNode = im.value.getSelectedNode();
        const containerNode = currentNode.closest(`[data-${PLUGIN_KEY}]`);
        im.value.selectNode(containerNode);
        e.preventDefault()
      };

      const keyHandler = (e: KeyboardEvent) => {
        const currentNode = im.value.getSelectedNode();

        if (isContainerNode(currentNode) && !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
          e.preventDefault();
        } else if (isChildNode(currentNode)) {
          selectContainer(e);
        }
      };

      on('keyup', keyHandler);
      on('keydown', keyHandler);

      on('click', (e) => {
        const currentNode = im.value.getSelectedNode();

        if (isChildNode(currentNode)) {
          selectContainer(e);
        }
      });
    });
};

const { formatPrice } = usePrice();

const removeSelectedProductCards = () => {
  im.value.deleteNodeContent()
  let currentNode = im.value.getSelectedNode();

  // Since caret can start from middle of product cards, we need to traverse (and delete)
  // each node on the way up to the container node.
  // oxlint-disable-next-line eslint(no-unmodified-loop-condition) -- PLUGIN_KEY is a constant; loop terminates via currentNode reassignment
  while (currentNode && (currentNode?.hasAttribute(`data-${PLUGIN_KEY}-child`) || currentNode?.hasAttribute(`data-${PLUGIN_KEY}`))) {
    im.value.deleteNode();
    im.value.selectNodeDepth(1);
    currentNode = im.value.getSelectedNode();
  }
}

const handleSubmit = (products: { uuid: string; name: string; imgSrc?: string; price: Amount; link: string }[], shouldShowPrices: boolean) => {
  const preppedProducts = products.map((product) => ({
    ...product,
    imgSrc: product.imgSrc,
    formattedPrice: shouldShowPrices ? formatPrice(product.price) : undefined,
    price: product.price,
    uuid: product.uuid,
    link: product.link,
  }));

  const content = getContent(preppedProducts, PLUGIN_KEY, imageCdnService.value);

  // Remove previous product cards and replace with new
  if (modalMode.value === ModalMode.Edit) {
    removeSelectedProductCards();
  }

  im.value.insertContent(content);
  emit('contentInserted', PLUGIN_KEY);

  closeProductCardDrawer();
};

const openProductCardDrawer = async () => {
  const preSelectedProducts: { uuid: string; name: string; imgSrc?: string; price: Amount; link: string }[] = [];

  if (modalMode.value === ModalMode.Edit) {
    try {
      Array.from(selectedNode.value.querySelectorAll('[data-aks-properties]')).forEach((node) => {
          const properties = JSON.parse(node.getAttribute('data-aks-properties') || '{}');

          preSelectedProducts.push({
            uuid: properties.uuid,
            name: properties.name,
            imgSrc: properties.imgSrc,
            price: properties.price,
            link: properties.link,
          });
      });
    } catch {
      // Noop if element did not contain valid JSON
    }
  }

  const props = {
    onSubmit: handleSubmit,
    onClose: closeProductCardDrawer,
    preSelectedProducts,
    imageCdnService: imageCdnService.value,
  };

  productCardDrawer.openDrawer(() => h(ProductCardDrawer, props), {
    right: true,
    width: '60%',
    closeOnOutsideClick: false,
  });
};

const closeProductCardDrawer = async () => {
  productCardDrawer.closeDrawer();
};

watchEffect(() => {
  if (im.value && pluginId.value) {
    register();
  }
});

</script>
<template>
  <span />
</template>

<style scoped lang="scss">
.insert-image-modal__content {
  @apply ds-text-left;

  .insert-image-modal__title {
    @apply ds-mb-4 ds-text-center;
  }

  .insert-image-modal__description {
    @apply ds-mb-4 ds-text-center;
  }

  .insert-image-modal__form {
    @apply ds-flex ds-gap-3 ds-flex-wrap;
  }

  .insert-image-modal__input-row {
    @apply ds-flex ds-gap-4 ds-items-center;

    i {
      @apply ds-cursor-pointer;
    }
  }

  .insert-image-modal__input {
    @apply ds-mb-4;
  }

  .insert-image-modal__actions {
    @apply ds-w-full ds-flex ds-justify-center ds-gap-4;
  }
}
</style>
